<template>
<div>
  <h1>Property Detail</h1>
</div>
</template>

<script lang="ts">
import { mapActions } from 'pinia';
import { ROUTES } from '~/constants/ui.constants';
import { usePropertiesStore } from '~/units/properties/stores';
import type { Component } from '~/types';
import { ComponentMixin } from '~/mixins/component.mixin';
import { PropertyMixin } from '~/units/properties/mixins/property.mixin';
import FloorPlan from '~/components/common/FloorPlan.vue';
import NeuronGoogleMap from '~/components/common/NeuronGoogleMap.vue';
import Epc from '~/components/common/Epc.vue';
import NcSelect from '~/components/common/inputs/NcSelect.vue';
import NcInput from '~/components/common/inputs/NcInput.vue';
import NcButton from '~/components/common/inputs/NcButton.vue';
import FullScreenGallery from '~/components/common/FullScreenGallery.vue';
import { useCoreStore } from '~/units/core/store';
import SendLead from '~/components/common/leads/SendLead.vue';
import Success from '~/components/common/popup/Success.vue';
import Popup from '~/components/common/popup/Popup.vue';
import PropertyDetailActions from '~/components/theme4/properties/PropertyDetailActions.vue';
import AvailableComponentsForSidebar from '~/components/theme4/AvailableComponentsForSidebar.vue';
import { FullScreenPopupMixin } from '~/units/core/mixins/fullScreenPopupMixin';
import { getImagesByCount } from '~/units/properties/store';

export default defineNuxtComponent({
  name: 'Theme9PropertyDetail',
  components: {
    AvailableComponentsForSidebar,
    PropertyDetailActions,
    Popup,
    Success,
    SendLead,
    FullScreenGallery,
    NcButton,
    NcInput,
    NcSelect,
    Epc,
    NeuronGoogleMap,
    FloorPlan,
  },

  mixins: [ComponentMixin, PropertyMixin, FullScreenPopupMixin],

  data() {
    return {
      activeTab: { name: 'features', title: 'FEATURES', is_visible: !!this.featuredVideo },
      activeTabV2: { name: 'features', title: 'FEATURES' },
      swiper: {},
      isScrolled: false,
      scrollThreshold: 0,
      variants: {
        v1: 'v1',
        v2: 'v2',
      },
      videoPopupConfig: {
        isActive: false,
        videoPopupSrc: this.featuredVideo,
      },
    };
  },

  mounted() {
    this.setScrollThreshold();
    window.addEventListener('scroll', this.handleScroll);
    this.handleScroll();
  },

  computed: {
    tabs() {
      const tabs = [
        {
          name: 'features',
          title: 'FEATURES',
          is_visible: !!this.property?.features,
        },
        {
          name: 'property_information',
          title: 'PROPERTY INFO',
          is_visible: true,
        },
        {
          name: 'floor_plan',
          title: 'FLOOR PLANS',
          is_visible: !!this.property?.floor_plans?.length,
        },
        {
          name: 'map',
          title: 'MAP',
          is_visible: !!this.property.map_position,
        },
        {
          name: 'epc',
          title: 'EPC',
          is_visible: !!this.property?.epcs?.length,
        },
        {
          name: 'matterport_video',
          title: 'MATTERPORT VIDEO',
          is_visible: !!this.matterPort,
        },
        {
          name: 'video_tour',
          title: 'VIDEO TOUR',
          is_visible: !!this.featuredVideo,
        },
      ];

      return tabs.filter((i) => i.is_visible);
    },

    tabsV2() {
      const tabs = [
        {
          name: 'cinematic_videography',
          title: 'CINEMATIC VIDEO',
          is_visible: !!this.featuredVideo,
        },
        {
          name: 'features',
          title: 'FEATURES',
          is_visible: !!this.property?.features,
        },
        {
          name: 'property_information',
          title: 'PROPERTY INFO',
          is_visible: true,
        },
        {
          name: 'floor_plan',
          title: 'FLOOR PLANS',
          is_visible: !!this.property?.floor_plans?.length,
        },
        {
          name: 'map',
          title: 'MAP',
          is_visible: !!this.property.map_position,
        },
        {
          name: 'epc',
          title: 'EPC',
          is_visible: !!this.property?.epcs?.length,
        },
        /*         {
          name: 'matterport_video',
          title: 'MATTERPORT VIDEO',
          is_visible: !!this.matterPort,
        }, */
      ];

      return tabs.filter((i) => i.is_visible);
    },

    markerColor() {
      return this.colors.primary;
    },

    ROUTES() {
      return ROUTES;
    },

    primaryFivePhotos() {
      if (this.property) {
        return getImagesByCount(this.property, 5);
      }
      return [];
    },

    allPhotos() {
      if (this.property) {
        return getImagesByCount(this.property, Math.min(this.property.medias.length, 10));
      }
      return [];
    },

    primarySevenPhotos() {
      if (this.property) {
        return getImagesByCount(this.property, 7);
      }
      return [];
    },

    sidebarComponents(): Component[] {
      if (this.currentPage && this.currentPage.sidebar) {
        return this.currentPage.sidebar;
      }
      return [];
    },

    isMortgageCalculatorVisible() {
      return !this.vars.isMortgageCalculatorHiddenNumber;
    },

    isStampDutyCalculatorVisible() {
      return !this.vars.isStampDutyCalculatorHiddenNumber;
    },

    isLetterPropertyCriteria() {
      return this.vars.isLetterPropertyCriteriaNumber;
    },

    featuredVideo() {
      const mediaLink = this.property?.featured_video?.mediaLink;
      if (mediaLink && mediaLink.includes('matterport')) {
        return null;
      }
      return mediaLink;
    },

    matterPort() {
      let mediaLink = this.property?.virtual_tour?.mediaLink;
      if (mediaLink && mediaLink.includes('youtu')) {
        return null;
      }
      if (mediaLink && mediaLink.startsWith('http://')) {
        mediaLink = mediaLink.replace('http://', 'https://');
      }
      return mediaLink;
    },
  },

  methods: {
    ...mapActions(useCoreStore, ['shareApi', 'openTab']),
    async onShared() {
      await this.shareApi({
        title: this.property?.advert_heading,
        text: this.property?.advert_heading,
        url: window.location.href,
      });
    },

    onSwiper(swiper: object) {
      this.swiper = swiper;
    },

    href(location) {
      this.activeTab.name = location;
    },

    handleScroll() {
      this.isScrolled = window.scrollY > this.scrollThreshold;
    },

    setScrollThreshold() {
      nextTick(() => {
        const ulElement = this.$refs.navTabs;
        if (ulElement) {
          this.scrollThreshold = ulElement.offsetTop - 90;
        }
      });
    },

    toggleHeader() {
      if(this.property.is_locked) {
        return;
      }
      document.body.classList.toggle('popup-open');
    },

    onVidePopup() {
      this.videoPopupConfig.isActive = !this.videoPopupConfig.isActive;
      this.videoPopupConfig.videoPopupSrc = this.featuredVideo;
    },

    stopVideo() {
      this.videoPopupConfig.videoPopupSrc = '';
    },
  },

  async setup(props: { component: Component }) {
    const store = usePropertiesStore();
    await store.fetchSingleProperty(props.component);
  },

  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
  },
});
</script>
